import { Fragment, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import { useSelector } from "react-redux";

import Analytic from "@commons/cards/analytic";
import Grid from "@components/grid";
import Column from "@components/column";
import Row from "@components/row";
import Typography from "@components/typography";
import DropDown from "@components/fields/dropdown";

import theme from "@utils/theme";
import useIsMobile from "@hooks/useIsMobile";
import { RootState } from "@store";
import { Dashboard } from "@apis/index";

const DashboardCard = ({
  cardConfig = [],
  titleConfig,
  zoneId,
  setZoneId,
  moduleId,
  startDate,
  endDate,
  dateFilter,
  setDateFilter,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  filterOptions,
}: {
  cardConfig?: any;
  titleConfig: any;
  zoneId: number | string;
  setZoneId: (id: number | string) => void;
  moduleId?: number;
  startDate?: string;
  endDate?: string;
  dateFilter: string;
  setDateFilter: (val: string) => void;
  customStartDate: string;
  setCustomStartDate: (val: string) => void;
  customEndDate: string;
  setCustomEndDate: (val: string) => void;
  filterOptions: string[];
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const { data: dashboard, loading } = useSelector(
    (state: RootState) => state.dashboard,
  );
  const { data: zones } = useSelector((state: RootState) => state.zones);

  const fetchDashboard = async () => {
    try {
      const params: any = {};
      if (moduleId) params.moduleId = moduleId;
      if (zoneId && zoneId !== "all") params.zoneId = zoneId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      await Dashboard.summary({
        state: "dashboard",
        params,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [moduleId, zoneId, startDate, endDate]);

  return (
    <Fragment>
      <Column stylecolumn={isMobile ? "gap:15px;" : "gap:0;"}>
        {/* Header */}
        <Row
          styleRow={
            isMobile
              ? "flex-direction:column;align-items:flex-start;gap:10px;"
              : "justify-content:space-between;align-items:center;"
          }
        >
          {/* Left Side: Icon and Title */}
          <Row
            styleRow={
              isMobile
                ? "gap:10px;align-items:flex-start;"
                : "gap:10px;align-items:center;"
            }
          >
            <img src={titleConfig.icon} alt="" width={isMobile ? 40 : 50} />
            <Column stylecolumn="gap:0;">
              <Typography
                weight={600}
                size={isMobile ? 18 : 22}
                styleText={`color:${theme.orange.orange800}`}
              >
                {titleConfig.title} Dashboard
              </Typography>
              <Typography
                size={isMobile ? 12 : 14}
                styleText={`color:${theme.orange.orange900}`}
              >
                Hello! Here You Can Manage Your {titleConfig.title} Orders by
                Zone.
              </Typography>
            </Column>
          </Row>

          {/* Right Side: Date Filter and DropDown */}
          <Row styleRow="gap:15px;align-items:center;">
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: "4px",
                borderRadius: "8px",
                gap: "4px",
              }}
            >
              {filterOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => setDateFilter(option)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                    border: "none",
                    transition: "all 0.2s",
                    backgroundColor:
                      dateFilter === option ? "white" : "transparent",
                    color: dateFilter === option ? "#0f172a" : "#64748b",
                    boxShadow:
                      dateFilter === option
                        ? "0 1px 2px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
            {dateFilter === "Custom Time" && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                />
                <span style={{ color: "#64748b" }}>to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                />
              </div>
            )}
            <div
              style={{
                width: isMobile ? "100%" : "160px",
                minWidth: isMobile ? "none" : "150px",
              }}
            >
              <Formik initialValues={{ zone: zoneId }} onSubmit={() => {}}>
                {({ setFieldValue }) => (
                  <Form>
                    <DropDown
                      placeholder="All Zones"
                      hasSearch
                      name="zone"
                      options={[...(zones || [])]}
                      titleAsKey="name"
                      valueAsKey="id"
                      styleInput={`width:${isMobile && "97%"}`}
                      styleWrapper="width:90%"
                      onChange={(val: any) => {
                        setFieldValue("zone", val);
                        setZoneId(val === "all" ? "all" : val);
                      }}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </Row>
        </Row>
        <br />
        {/* Cards Grid */}
        <Grid>
          {cardConfig?.map((card: any, i: number) => (
            <Analytic
              loading={loading}
              key={i}
              title={card.title}
              icon={card.icon}
              value={dashboard?.[card.key]?.total ?? 0}
              newCount={dashboard?.[card.key]?.new ?? 0}
              isUp={card.isUp}
              onClick={() => navigate(card.link)}
            />
          ))}
        </Grid>
      </Column>
    </Fragment>
  );
};

export default DashboardCard;
